/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Typography, Box, Divider, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { useTheme } from '@emotion/react';
import SearchInput from '../../components/inputs/searchInput';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import debounce from 'lodash.debounce';
import Popup from '../../components/popups/popup';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import OnOffSwitchForDeleted from '../../components/inputs/onOffSwitchForDeleted';
import BlockedNumberService from '../../services/blockedNumberService';
import UpdateBlockedNumberBox from '../../components/boxes/blockedNumber/updateBlockedNumberBox';
import AddBlockedNumberBox from '../../components/boxes/blockedNumber/addBlcokedNumberBox';
import DeleteBlockedNumberModal from '../../components/modals/blockedNumber/deleteBlockedNumberModal';

const BlockedNumbers = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isEn = i18n.language === 'en';
    const tableHeadingStyle = { fontWeight: 'bold', backgroundColor: theme.palette.customGrey.tableHeading, cursor: 'pointer' };
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [blockedNumbers, setBlockedNumbers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedBlockedNumber, setSelectedBlockedNumber] = useState({});
    const [sortState, setSortState] = useState({ sortBy: '', order: '' });

    const [openUpdate, setOpenUpdate] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [deleted, setDeleted] = useState(false);

    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const tableContainerRef = useRef(null);
    const isFirstRender = useRef(true);

    const [showPopup, setShowPopup] = useState(false);
    const [popupMsg, setPopupMsg] = useState(false);
    const [popupType, setPopupType] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const onSort = (sort) => {
        let order = '';
        if (sort === sortState.sortBy || sortState.sortBy === '') {
            switch (sortState.order) {
                case '':
                    order = 'asc';
                    break;
                case 'asc':
                    order = 'desc';
                    break;
                case 'desc':
                    order = '';
                    break;
                default:
                    order = '';
                    break
            }
        }

        setSortState({ sortBy: order === '' ? '' : sort, order });
    };

    const sortIcon = () => {
        let icon = <></>;
        switch (sortState.order) {
            case 'asc':
                icon = <ArrowDownwardOutlinedIcon fontSize="small" />;
                break;
            case 'desc':
                icon = <ArrowUpwardOutlinedIcon fontSize="small" />;
                break;
            default:
                icon = <ArrowDownwardOutlinedIcon fontSize="small" sx={{ color: 'transparent' }} />;
                break
        }

        return icon;
    };

    const handleOpenUpdate = (blockedNumber) => {
        setSelectedBlockedNumber(blockedNumber);
        setOpenUpdate(true);
    };

    const handleReload = async () => {
        setOpenUpdate(false);
        setHasMore(true);
        setPage(1);
        setSearchTerm('');
        await fetchBlockedNumbers();
    };

    const handleOpenDelete = (blockedNumber) => {
        setSelectedBlockedNumber(blockedNumber);
        setOpenDelete(true);
    };
    const handleCloseDelete = async ({ isReaload }) => {
        setOpenDelete(false);
        if (isReaload) {
            setHasMore(true);
            setPage(1);
            setSearchTerm('');
            await fetchBlockedNumbers();
        }
    };


    const fetchBlockedNumbers = async (page = 1, name = '') => {
        try {
            if (isLoading) return;
            setIsLoading(true);
            const filters = { name, ...sortState, deleted };
            const data = await BlockedNumberService.fetch(page, filters);

            if (data.length === 0) {
                setHasMore(false);
                if (page === 1) {
                    setBlockedNumbers([]);
                }
            } else {
                setBlockedNumbers((prevs) => (page === 1 ? data : [...prevs, ...data]));
            }
        } catch (error) {
            console.error('Error fetching blokced numbers:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const debouncedFetchData = useMemo(() =>
        debounce((name) => {
            setPage(1);
            setHasMore(true);
            fetchBlockedNumbers(1, name);
        }, 1000),
        []
    );


    const handleSearchChange = (value) => {
        setSearchTerm(value);
        debouncedFetchData(value);
    };

    const resetFilter = () => {
        setSearchTerm('');
        setHasMore(true);
        if (page !== 1) setPage(1);
        else fetchBlockedNumbers(1, '');
    };

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (page === 1) fetchBlockedNumbers(page, searchTerm);
    }, [page]);

    useEffect(() => {
        if (!isFirstRender.current) resetFilter();
    }, [sortState, deleted]);

    const handleScroll = (event) => {
        const container = event.currentTarget;

        if (container.scrollTop + container.clientHeight >= container.scrollHeight - 50) {
            if (hasMore && !isLoading) {
                fetchBlockedNumbers(page + 1, searchTerm);
                setPage(prevPage => prevPage + 1);
            }
        }
    };

    return (
        <Box sx={{
            p: 2,
            backgroundColor: theme.palette.secondary.main,
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <DeleteBlockedNumberModal open={openDelete} handleClose={handleCloseDelete} setPopupMsg={setPopupMsg}
                    setPopupType={setPopupType}
                    setShowPopup={setShowPopup} blockedNumber={selectedBlockedNumber} />
            </Box>
            {showPopup && (
                <Popup
                    text={popupMsg}
                    type={popupType}
                    onClose={() => setShowPopup(false)}
                />
            )}

            {isTablet && <Box>
                {
                    (selectedBlockedNumber && openUpdate) ?
                        <UpdateBlockedNumberBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                            setPopupType={setPopupType}
                            setShowPopup={setShowPopup} blokcedNumber={selectedBlockedNumber} />
                        :
                        <AddBlockedNumberBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                            setPopupType={setPopupType}
                            setShowPopup={setShowPopup} />
                }
            </Box>}

            {/* Filter row */}
            {
                isMobile ?
                    <Box
                        sx={{
                            my: 2,
                            border: 1,
                            borderColor: theme.palette.customGrey.borderInput,
                            backgroundColor: 'white',
                            borderRadius: 1,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', height: 50 }}>
                            <Box sx={{ px: 2 }}>
                                <FilterAltOutlinedIcon />
                            </Box>
                            <Divider orientation="vertical" />
                            <Box width={isTablet ? '70%' : '80%'}>
                                <SearchInput placeholder={t('name')} onChange={handleSearchChange} value={searchTerm} />
                            </Box>
                        </Box>
                        <Divider />

                        <Box sx={{ display: 'flex', alignItems: 'center', height: 50 }}>
                            <Box sx={{
                                ml: isEn ? 3 : 0
                            }}>
                                <OnOffSwitchForDeleted
                                    checkDeleted={deleted}
                                    setCheckDeleted={setDeleted} />
                            </Box>
                            <Divider orientation="vertical" sx={{ mr: isEn ? 0 : 3 }} />
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    color: theme.palette.text.red,
                                    px: 2,
                                    cursor: 'pointer',
                                }}
                                onClick={resetFilter}
                            >
                                <ReplayOutlinedIcon />
                                <Box width={3}></Box>
                                <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                            </Box>
                        </Box>
                    </Box>
                    :
                    <Box
                        sx={{
                            my: 2,
                            display: 'flex',
                            alignItems: 'center',
                            border: 1,
                            borderColor: theme.palette.customGrey.borderInput,
                            backgroundColor: 'white',
                            borderRadius: 1,
                            height: 60,
                        }}
                    >
                        <Box sx={{ px: 2 }}>
                            <FilterAltOutlinedIcon />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={isTablet ? '70%' : '80%'}>
                            <SearchInput placeholder={t('name')} onChange={handleSearchChange} value={searchTerm} />
                        </Box>
                        <Divider orientation="vertical" sx={{ mr: isEn ? 3 : 0 }} />

                        <OnOffSwitchForDeleted
                            checkDeleted={deleted}
                            setCheckDeleted={setDeleted} />
                        <Divider orientation="vertical" sx={{ mr: isEn ? 0 : 3 }} />
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: theme.palette.text.red,
                                px: 2,
                                cursor: 'pointer',
                            }}
                            onClick={resetFilter}
                        >
                            <ReplayOutlinedIcon />
                            <Box width={3}></Box>
                            <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                        </Box>
                    </Box>
            }

            <Box sx={{
                display: 'flex'
            }}>
                {!isTablet && <Box sx={{
                    width: '35%'
                }}>
                    {
                        (selectedBlockedNumber && openUpdate) ?
                            <UpdateBlockedNumberBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                                setPopupType={setPopupType}
                                setShowPopup={setShowPopup} blokcedNumber={selectedBlockedNumber} />
                            :
                            <AddBlockedNumberBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                                setPopupType={setPopupType}
                                setShowPopup={setShowPopup} />
                    }
                </Box>}

                {!isTablet && <Box width={10}></Box>}

                {/* Scrollable Table with Sticky Header */}
                <TableContainer
                    component={Paper}
                    ref={tableContainerRef}
                    onScroll={handleScroll}
                    sx={{
                        boxShadow: 'none',
                        border: 1,
                        borderColor: theme.palette.customGrey.borderInput,
                        borderRadius: 2,
                        height: 'calc(100vh - 170px)',
                        overflowY: 'auto',
                        width: isTablet ? '100%' : '65%'
                    }}
                >
                    <Table stickyHeader aria-label="simple table">
                        <TableHead
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                            }}
                        >
                            <TableRow>
                                <TableCell align={isEn ? 'left' : 'right'}
                                    onClick={() => onSort('id')} sx={tableHeadingStyle}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('id')}
                                        {sortState.sortBy === 'id' && sortIcon()}
                                    </Box>
                                </TableCell>
                                <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                    onClick={() => onSort('phone')}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('phone')}
                                        {sortState.sortBy === 'phone' && sortIcon()}
                                    </Box>
                                </TableCell>

                                <TableCell align={isEn ? 'left' : 'right'} sx={{ ...tableHeadingStyle, cursor: 'auto' }}>
                                    {t('actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {blockedNumbers.map((blockedNumber, index) => (
                                <TableRow
                                    key={blockedNumber.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell align={isEn ? 'left' : 'right'} component="th" scope="row">{blockedNumber.id}</TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>{blockedNumber.phone}</TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>
                                        <Box sx={{
                                            display: 'inline-flex',
                                            border: 1,
                                            borderColor: theme.palette.customGrey.borderInput,
                                            backgroundColor: theme.palette.secondary.main,
                                            borderRadius: 1,
                                        }}>
                                            {blockedNumber.deletedAt === null && <Box sx={{
                                                px: 1.5,
                                                pt: 0.4,
                                                pb: 0.25,
                                                cursor: 'pointer'
                                            }} onClick={() => handleOpenUpdate(blockedNumber)}>
                                                <DriveFileRenameOutlineOutlinedIcon fontSize='small'
                                                    sx={{ color: theme.palette.text.primary }} />
                                            </Box>}
                                            {blockedNumber.deletedAt === null && <Divider orientation="vertical" flexItem />}
                                            <Box sx={{
                                                px: 1.5,
                                                pt: 0.4,
                                                pb: 0.25,
                                                cursor: 'pointer'
                                            }} onClick={() => handleOpenDelete(blockedNumber)}>
                                                {blockedNumber.deletedAt === null ?
                                                    <DeleteOutlineOutlinedIcon fontSize='small'
                                                        sx={{ color: theme.palette.red.bg }} />
                                                    :
                                                    <ReplayOutlinedIcon fontSize='small'
                                                        sx={{ color: theme.palette.green.bg }} />
                                                }
                                            </Box>

                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

            </Box>


        </Box>
    );
};

export default BlockedNumbers;
